import { InjectionToken } from '@angular/core';
import { ProgramTypeKey } from '@k12/models';
import { Observable } from 'rxjs';

/**
 * What a portal must supply for EnrollmentCompleteComponent to know when provisioning has landed.
 *
 * Every registration program is provisioned asynchronously — SubmitEnrollmentForm dispatches
 * RegistrationSubmissionWorkflow and returns without waiting — so the dashboard can load before the
 * domain graph exists. The only per-portal difference is which record to watch for, which is why this
 * is a token rather than three copies of the same polling page.
 */
export interface EnrollmentReadiness {
  /**
   * The record being provisioned, lowercase, as it reads mid-sentence: "creating your school record".
   */
  readonly recordNoun: string;

  /** The portal's registration program, used to recover the session id when the URL omits it. */
  readonly programType: ProgramTypeKey;

  /**
   * Emits true once the record is readable. A 404 while the saga is still running is expected, so
   * implementations should resolve their own errors to false rather than throwing — the component
   * treats an error as "not ready yet" either way.
   */
  isReady(): Observable<boolean>;

  /**
   * Called once, after `isReady` first answers true and before the dashboard navigation.
   *
   * Each portal's shell reads its account-scoped data once at bootstrap — before this record
   * existed — and submit router-navigates rather than reloading, so nothing re-reads it. Without
   * this the registrant lands on a shell built for someone who has no record: the providers nav
   * falls back to its Home-only `notStartedMenu`, and the schools nav is built from an empty
   * institution list.
   *
   * Optional because a portal whose shell holds nothing account-scoped has nothing to re-read.
   * Implementations must not throw: a failure here should cost a stale nav, not the navigation.
   */
  onProvisioned?(): void;
}

export const ENROLLMENT_READINESS = new InjectionToken<EnrollmentReadiness>('ENROLLMENT_READINESS');
